import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router';
import { RejectedReason } from 'components/FileUploader';
import LinkButton from 'components/LinkButton';
import UploadDimensionsRecommendation from 'components/UploadDimensionsRecommendation';
import Wizard, { Step } from 'components/Wizard';
import { MediaImportedArgs } from 'containers/MediaUploadModal/types';
import WaveformGenerationError from 'redux/factories/entire-video/WaveformGenerationError';
import * as mixpanelActions from 'redux/modules/mixpanel/actions';
import { showError } from 'redux/modules/notification';
import { tierSelector } from 'redux/modules/pricing';
import { entireVideoEnhancer } from 'redux/modules/video-upload';
import { clearCreateCreationAsset } from 'redux/modules/wizard-export/actions';
import { Dispatch } from 'redux/types';
import { ApplicationError } from 'utils/ApplicationError';
import { DropZoneType, SAMPLE_VIDEO_URL } from 'utils/constants';
import { addParamsToUrl, revokeObjectUrl } from 'utils/url';
import UploadVideoStep, {
  UploadVideoStepProps,
} from '../../../UploadVideoStep';
import { VideoWizardObjective } from '../../types';
import VideoWizardNoMostReplayedData from '../../VideoWizardNoMostReplayedData';
import VideoWizardYoutubeEmbed from '../../VideoWizardYoutubeEmbed';
import {
  isYoutubeMostReplayedObjectiveSelector,
  shouldShowNoMostReplayedDataMessageSelector,
  shouldShowVideoSizeRecommendationSelector,
  shouldSkipDestinationForImportedMediaSelector,
  VideoFlowAction,
  VideoFlowState,
  youtubeEmbedVideoIdSelector,
} from '../state';
import { VideoFlowStep } from '../types';
import { block } from '../utils';

export type UseUploadVideoStepProps = Pick<UploadVideoStepProps, 'onError'> & {
  objective?: VideoWizardObjective;
  state: VideoFlowState;
  stateDispatch: React.Dispatch<VideoFlowAction>;
  wizardRef: React.RefObject<Wizard>;
};

export default function useUploadVideoStep({
  objective,
  onError,
  state,
  stateDispatch,
  wizardRef,
}: UseUploadVideoStepProps) {
  const dispatch = useDispatch<Dispatch>();
  const location = useLocation<{ file?: any }>();
  const history = useHistory();
  const tier = useSelector(tierSelector);

  const [useSampleAsSrc, setUseSampleAsSrc] = useState(false);
  const isYoutubeMostReplayedFlow = isYoutubeMostReplayedObjectiveSelector(
    state,
  );
  const shouldSkipDestination = shouldSkipDestinationForImportedMediaSelector(
    state,
  );
  const showVideoSizeRecommendation = shouldShowVideoSizeRecommendationSelector(
    state,
  );
  const shouldShowNoMostReplayedData = shouldShowNoMostReplayedDataMessageSelector(
    state,
  );
  const youtubeEmbedVideoId = youtubeEmbedVideoIdSelector(state);

  const {
    aspectRatioName,
    language,
    mediaImportedArgs,
    videoSrc,
    videoSrcUrl,
  } = state;

  const noMostReplayedDataMessage = shouldShowNoMostReplayedData && (
    <VideoWizardNoMostReplayedData />
  );

  const clearDefaultFile = useCallback(() => {
    if (location.state?.file) {
      const { state: locationState, ...locationWithoutState } = location;
      history.replace(locationWithoutState);
    }
  }, [history, location]);

  const setSource = useCallback(
    (src?: string, isSample: boolean = false) => {
      if (src !== videoSrcUrl) {
        dispatch(clearCreateCreationAsset());
        dispatch(entireVideoEnhancer.actions.clearEntireVideo());
        revokeObjectUrl(videoSrcUrl);
      }
      setUseSampleAsSrc(isSample);
    },
    [dispatch, videoSrcUrl],
  );

  // note - video-flow shared state is reset in the reducer when it sees the step
  // change action
  const reset = useCallback(() => {
    setSource(undefined);
  }, [setSource]);

  const handleSampleVideoClick = useCallback(() => {
    dispatch(
      mixpanelActions.onFileUpload({
        source: 'sampleFile',
        type: 'videoTranscription',
      }),
    );
    const src = addParamsToUrl(SAMPLE_VIDEO_URL, { nonce: Date.now() });
    setSource(src, true);
    stateDispatch({
      type: 'sample-video-select',
      payload: { src },
    });
    wizardRef.current?.next();
  }, [dispatch, setSource, stateDispatch, wizardRef]);

  const handleFileRejected = useCallback(
    (error: ApplicationError, file?: File, reason?: RejectedReason) => {
      clearDefaultFile();
      onError?.(error, file, reason);
    },
    [clearDefaultFile, onError],
  );

  const handleFileAccepted = useCallback(
    (file: File) => {
      clearDefaultFile();
      const srcUrl = URL.createObjectURL(file);
      setSource(srcUrl);
      stateDispatch({
        type: 'file-accepted',
        payload: { file, srcUrl },
      });
      dispatch(
        mixpanelActions.onFileUpload({
          dropZoneType: DropZoneType.VIDEO_TRANSCRIPTION,
          source: 'localFile',
          file,
        }),
      );
    },
    [clearDefaultFile, dispatch, setSource, stateDispatch],
  );

  const handleMediaAccepted = useCallback(
    (newMediaImportedArgs: MediaImportedArgs) => {
      if (!newMediaImportedArgs) {
        return;
      }

      const { previewUrl } = newMediaImportedArgs;

      setSource(previewUrl, false);
      stateDispatch({
        type: 'media-accepted',
        payload: { mediaImportedArgs: newMediaImportedArgs },
      });
    },
    [setSource, stateDispatch],
  );

  const uploadEntireVideoForWaveform = useCallback(() => {
    dispatch(
      entireVideoEnhancer.actions.uploadEntireVideo(
        videoSrc
          ? { language, src: videoSrc }
          : { language, mediaImportedArgs },
        'waveform',
      ),
    ).catch(error => {
      if (error instanceof WaveformGenerationError) {
        dispatch(showError({ message: error.message }));
      }
    });
  }, [dispatch, language, mediaImportedArgs, videoSrc]);

  const handleNextClick = useCallback(() => {
    // SingleClip uploads the entire video (and transcribes it) for the waveform
    // on the next step. This is deferred until the user leaves the upload step so
    // it uses the language the user actually selected, rather than the default
    // language that is set when the file is first accepted.
    if (objective === 'SingleClip') {
      uploadEntireVideoForWaveform();
    }

    if (shouldSkipDestination) {
      wizardRef.current?.jump(VideoFlowStep.CUSTOMIZE);
    } else {
      wizardRef.current?.next();
    }
  }, [
    objective,
    shouldSkipDestination,
    uploadEntireVideoForWaveform,
    wizardRef,
  ]);

  const cleanup = useCallback(() => {
    revokeObjectUrl(videoSrcUrl);
  }, [videoSrcUrl]);

  const step: Step<VideoFlowStep.UPLOAD_VIDEO> = {
    completedName: 'Video Selected',
    component: (
      <UploadVideoStep
        className={block('uploader')}
        defaultFile={location.state?.file}
        defaultMediaImportOption={
          isYoutubeMostReplayedFlow ? 'youtube' : undefined
        }
        footer={
          <LinkButton onClick={handleSampleVideoClick}>
            No video file? Try our sample.
          </LinkButton>
        }
        language={language}
        onError={handleFileRejected}
        onFileAccepted={handleFileAccepted}
        onLanguageSelect={newLanguage =>
          stateDispatch({
            type: 'language-select',
            payload: { language: newLanguage },
          })
        }
        onMediaAccepted={handleMediaAccepted}
        openMediaUploadModalOnInit={isYoutubeMostReplayedFlow}
        selectedMessage={
          youtubeEmbedVideoId ? (
            <div className={block('youtube-embed-wrapper')}>
              <VideoWizardYoutubeEmbed videoId={youtubeEmbedVideoId} />
              {noMostReplayedDataMessage}
            </div>
          ) : (
            <UploadVideoStep.SelectedMessage>
              {noMostReplayedDataMessage}
            </UploadVideoStep.SelectedMessage>
          )
        }
        templateDimensionsInfo={
          showVideoSizeRecommendation &&
          aspectRatioName && (
            <UploadDimensionsRecommendation aspectRatio={aspectRatioName} />
          )
        }
        tier={tier}
        uploadMethod="mediaImporter"
        /*
         * clicking the sample video should automatically advance to the next step, so the
         * isSampleVideo check is here to prevent the uploader from flashing the "selected
         * video" state when transitioning to the next step
         */
        videoSelected={!useSampleAsSrc && videoSrcUrl !== undefined}
      />
    ),
    name: 'Upload Video',
    renderNextButton: props => (
      <Wizard.Button
        {...props}
        disabled={videoSrcUrl === undefined}
        onClick={handleNextClick}
        theme="next"
      >
        Next
      </Wizard.Button>
    ),
    stepId: VideoFlowStep.UPLOAD_VIDEO,
  };

  return {
    cleanup,
    step,
    reset,
  };
}
